import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    Card,
    Typography,
    Button,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Tooltip
} from '@material-tailwind/react';
import { FaEye, FaRedo, FaCheck, FaTrash, FaChevronLeft, FaChevronRight, FaPlayCircle, FaInfoCircle, FaEllipsisV } from 'react-icons/fa';
import { formatAmount, formatDate } from './utils/formatters';
import { getPostingStatus } from './utils/postingStatus';

const TransMessagesTable = ({
    messages,
    loading,
    pagination,
    onPageChange,
    onViewMessage,
    onRetryMessage,
    onDeleteMessage,
    onMarkAsUsed,
    onRequeueMessage,
    onBulkRequeue
}) => {
    const formatCompactDate = (value) => {
        if (!value) return 'N/A';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return formatDate(value);
        }

        const pad = (num) => String(num).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };

    const getStatusIconMeta = (status) => {
        const normalized = (status || '').toLowerCase();
        if (normalized === 'posted' || normalized === 'success') {
            return {
                icon: <FaCheck className="h-3 w-3" />,
                className: 'text-green-600 bg-green-50 border-green-200',
                label: status || 'posted'
            };
        }
        if (normalized === 'pending' || normalized === 'retrying') {
            return {
                icon: <FaRedo className="h-3 w-3" />,
                className: 'text-amber-600 bg-amber-50 border-amber-200',
                label: status || 'pending'
            };
        }
        return {
            icon: <FaInfoCircle className="h-3 w-3" />,
            className: 'text-red-600 bg-red-50 border-red-200',
            label: status || 'failed'
        };
    };

    // Pagination breakdown (with safe defaults)
    const { currentPage = 1, totalPages = 1, totalMessages = 0 } = pagination || {};

    // Popup state for content/error dialogs
    const [popupContent, setPopupContent] = useState(null);
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupTitle, setPopupTitle] = useState('');

    // Selection state for bulk requeue
    const [selectedMessages, setSelectedMessages] = useState(new Set());
    const [selectAll, setSelectAll] = useState(false);
    // Mobile action menu state (which message's menu is open)
    const [openMenu, setOpenMenu] = useState(null);

    const showPopup = (title, content) => {
        setPopupTitle(title);
        setPopupContent(content);
        setPopupOpen(true);
    };

    const closePopup = () => {
        setPopupOpen(false);
        setPopupContent(null);
        setPopupTitle('');
    };

    // Messages eligible for requeue (eval=true and no posted_trans_id)
    const requeueEligibleMessages = Array.isArray(messages) ? messages.filter(msg => msg.eval && !msg.posted_trans_id) : [];

    const handleSelectAll = (checked) => {
        setSelectAll(checked);
        if (checked) {
            const eligibleIds = new Set(requeueEligibleMessages.map(msg => msg.id));
            setSelectedMessages(eligibleIds);
        } else {
            setSelectedMessages(new Set());
        }
    };

    const handleSelectMessage = (messageId, checked) => {
        const newSelected = new Set(selectedMessages);
        if (checked) newSelected.add(messageId);
        else newSelected.delete(messageId);
        setSelectedMessages(newSelected);
        setSelectAll(newSelected.size === requeueEligibleMessages.length && requeueEligibleMessages.length > 0);
    };
    const handleBulkRequeueClick = () => {
        if (selectedMessages.size > 0 && onBulkRequeue) {
            onBulkRequeue(Array.from(selectedMessages));
            setSelectedMessages(new Set());
            setSelectAll(false);
        }
    };

    useEffect(() => {
        if (!openMenu) return undefined;

        const closeMenu = () => setOpenMenu(null);

        const handlePointerDown = (event) => {
            const target = event.target;
            if (!(target instanceof Element)) return;
            if (target.closest('[data-trans-message-actions-menu="true"]')) return;
            if (target.closest('[data-trans-message-actions-trigger="true"]')) return;
            closeMenu();
        };

        const handleEscape = (event) => {
            if (event.key === 'Escape') closeMenu();
        };

        window.addEventListener('scroll', closeMenu, true);
        window.addEventListener('resize', closeMenu);
        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown, { passive: true });
        document.addEventListener('keydown', handleEscape);

        return () => {
            window.removeEventListener('scroll', closeMenu, true);
            window.removeEventListener('resize', closeMenu);
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [openMenu]);

    const openActionsMenu = (message, triggerEl) => {
        const rect = triggerEl?.getBoundingClientRect?.();
        if (!rect) {
            setOpenMenu({ messageId: message.id, top: 0, left: 0, openUp: false });
            return;
        }

        const menuWidth = 176;
        const viewportPadding = 12;
        const spaceAbove = rect.top;
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUp = spaceAbove > 220 && spaceAbove > spaceBelow;
        const left = Math.min(
            window.innerWidth - menuWidth - viewportPadding,
            Math.max(viewportPadding, rect.right - menuWidth)
        );

        setOpenMenu({
            messageId: message.id,
            top: openUp ? rect.top - 8 : rect.bottom + 8,
            left,
            openUp,
        });
    };

    const closeActionsMenu = () => setOpenMenu(null);

    const renderPaginationButtons = () => {
        const buttons = [];
        const maxButtons = 5;

        if (totalPages <= maxButtons) {
            for (let i = 1; i <= totalPages; i++) {
                buttons.push(
                    <Button
                        key={i}
                        variant={currentPage === i ? "filled" : "outlined"}
                        size="sm"
                        onClick={() => onPageChange(i)}
                        className="min-w-[2rem] px-2"
                    >
                        {i}
                    </Button>
                );
            }
        } else {
            let startPage = Math.max(1, currentPage - 2);
            let endPage = Math.min(totalPages, startPage + maxButtons - 1);

            if (endPage - startPage < maxButtons - 1) {
                startPage = Math.max(1, endPage - maxButtons + 1);
            }

            for (let i = startPage; i <= endPage; i++) {
                buttons.push(
                    <Button
                        key={i}
                        variant={currentPage === i ? "filled" : "outlined"}
                        size="sm"
                        onClick={() => onPageChange(i)}
                        className="min-w-[2rem] px-2"
                    >
                        {i}
                    </Button>
                );
            }
        }

        return buttons;
    };

    if (loading && (!messages || messages.length === 0)) {
        return (
            <Card className="w-full overflow-hidden">
                <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto"></div>
                    <Typography className="mt-4">Loading messages...</Typography>
                </div>
            </Card>
        );
    }

    return (
        <div className="flex flex-col gap-4 font-sans">
            {/* Bulk Actions Bar */}
            {selectedMessages.size > 0 && (
                <div className="flex-shrink-0 bg-white border border-gray-200 rounded-sm p-3 mb-2 shadow-none">
                    <div className="flex items-center justify-between">
                        <Typography variant="small" color="blue-gray" className="font-medium">
                            {selectedMessages.size} selected • ready to requeue
                        </Typography>
                        <div className="flex items-center gap-2">
                            <Button size="sm" onClick={handleBulkRequeueClick} className="flex items-center gap-2 bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-3 py-2 shadow-none normal-case">
                                <FaPlayCircle className="h-3 w-3" />
                                Requeue
                            </Button>
                            <Button size="sm" variant="outlined" onClick={() => { setSelectedMessages(new Set()); setSelectAll(false); }} className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-3 py-2 shadow-none border border-slate-200 normal-case">
                                Clear
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Desktop: table view (md+) */}
            <div className="hidden md:block w-full">
                <Card className="w-full bg-white shadow-lg rounded-xl border border-gray-100 overflow-visible">
                    <div className="overflow-x-auto overflow-y-visible">
                        <table className="w-full min-w-[1120px] table-auto text-left">
                            <thead>
                                <tr className="border-b border-blue-gray-100 bg-[#EDE4DA]">
                                    <th className="sticky top-0 z-10 rounded-tl-xl bg-[#EDE4DA] p-2 w-[40px] text-center">
                                        <input
                                            type="checkbox"
                                            checked={selectAll}
                                            onChange={(e) => handleSelectAll(e.target.checked)}
                                            disabled={requeueEligibleMessages.length === 0}
                                            className="w-4 h-4 text-[#FF5733] bg-gray-100 border-gray-300 rounded focus:ring-[#FF5733] cursor-pointer"
                                        />
                                    </th>
                                    <th className="sticky top-0 z-10 bg-[#EDE4DA] p-2 w-[56px]">
                                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none">Posted</Typography>
                                    </th>
                                    <th className="sticky top-0 z-10 bg-[#EDE4DA] p-2 w-[112px]">
                                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none">When</Typography>
                                    </th>
                                    <th className="sticky top-0 z-10 bg-[#EDE4DA] p-2 w-[52px] text-center">
                                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">Status</Typography>
                                    </th>
                                    <th className="sticky top-0 z-10 bg-[#EDE4DA] p-2 w-[88px]">
                                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none">Plate No.</Typography>
                                    </th>
                                    <th className="sticky top-0 z-10 bg-[#EDE4DA] p-2 w-[112px]">
                                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none">Code</Typography>
                                    </th>
                                    <th className="sticky top-0 z-10 bg-[#EDE4DA] p-2 w-[78px]">
                                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none">Driver</Typography>
                                    </th>
                                    <th className="sticky top-0 z-10 bg-[#EDE4DA] p-2 w-[88px] text-right">
                                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none">Amount</Typography>
                                    </th>
                                    <th className="sticky top-0 z-10 bg-[#EDE4DA] p-2 w-[52px] text-center">
                                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">Eval</Typography>
                                    </th>
                                    <th className="sticky top-0 z-10 bg-[#EDE4DA] p-2 w-[52px] text-center">
                                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">Used</Typography>
                                    </th>
                                    <th className="sticky top-0 z-10 rounded-tr-xl bg-[#EDE4DA] p-2 w-[60px] whitespace-nowrap text-center">
                                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none">Actions</Typography>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {(!messages || messages.length === 0) ? (
                                    <tr>
                                        <td colSpan="11" className="p-8 text-center">No messages found</td>
                                    </tr>
                                ) : (
                                    messages.map((message) => {
                                        const isEligibleForRequeue = message.eval && !message.posted_trans_id;
                                        const isSelected = selectedMessages.has(message.id);
                                        return (
                                            <tr key={message.id} className="border-b border-blue-gray-50 hover:bg-blue-gray-50/50 transition-colors">
                                                <td className="p-2 w-[40px] text-center align-middle">
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={(e) => handleSelectMessage(message.id, e.target.checked)}
                                                        disabled={!isEligibleForRequeue}
                                                        className="w-4 h-4 text-[#FF5733] bg-gray-100 border-gray-300 rounded focus:ring-[#FF5733] cursor-pointer"
                                                    />
                                                </td>
                                                <td className="p-2 align-middle w-[56px]">
                                                    <Tooltip content={message.posted_trans_id ? `Posted Trans ID: ${message.posted_trans_id}` : 'Not posted'}>
                                                        <span className={`inline-flex items-center justify-center h-7 min-w-[28px] px-2 rounded-full border text-xs font-semibold ${message.posted_trans_id ? 'text-green-700 bg-green-50 border-green-200' : 'text-slate-700 bg-slate-50 border-slate-200'}`}>
                                                            {message.posted_trans_id ? 'Y' : 'N'}
                                                        </span>
                                                    </Tooltip>
                                                </td>
                                                <td className="p-2 align-middle w-[112px]">
                                                    <Tooltip content={`${formatDate(message.timestamp || message.created_at)}\nID: ${message.id}`}>
                                                        <Typography variant="small" color="blue-gray" className="block max-w-[112px] truncate text-xs font-normal cursor-help">
                                                            {formatCompactDate(message.timestamp || message.created_at)}
                                                        </Typography>
                                                    </Tooltip>
                                                </td>
                                                <td className="p-2 w-[52px] text-center align-middle">
                                                    {(() => {
                                                        const statusMeta = getStatusIconMeta(getPostingStatus(message));
                                                        return (
                                                            <Tooltip content={message.posting_error ? `Status: ${statusMeta.label}\nError: ${message.posting_error}` : `Status: ${statusMeta.label}`}>
                                                                <span className={`inline-flex items-center justify-center h-7 w-7 rounded-full border ${statusMeta.className}`}>
                                                                    {statusMeta.icon}
                                                                </span>
                                                            </Tooltip>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="p-2 align-middle w-[88px]">
                                                    <Tooltip content={message.text || 'No message content'}>
                                                        <Typography variant="small" color="blue-gray" className="block max-w-[88px] truncate text-xs font-medium cursor-help">
                                                            {message.used_car || 'N/A'}
                                                        </Typography>
                                                    </Tooltip>
                                                </td>
                                                <td className="p-2 align-middle w-[112px]">
                                                    <Tooltip content={message.transaction_code || 'No transaction code'}>
                                                        <Typography variant="small" color="blue-gray" className="block max-w-[112px] truncate text-xs font-mono font-normal cursor-help">
                                                            {message.transaction_code || 'N/A'}
                                                        </Typography>
                                                    </Tooltip>
                                                </td>
                                                <td className="p-2 align-middle w-[78px]">
                                                    <Typography variant="small" color="blue-gray" className="block max-w-[78px] truncate text-xs font-normal">
                                                        {message.used_driver || 'N/A'}
                                                    </Typography>
                                                </td>
                                                <td className="p-2 align-middle w-[88px] text-right">
                                                    <Typography variant="small" color="blue-gray" className="block max-w-[88px] truncate text-xs font-medium">
                                                        {formatAmount(message.amount_extracted)}
                                                    </Typography>
                                                </td>
                                                <td className="p-2 w-[52px] text-center align-middle">
                                                    <span className={`inline-flex items-center justify-center h-7 w-7 rounded-full border text-xs font-semibold ${message.eval ? 'text-green-700 bg-green-50 border-green-200' : 'text-slate-700 bg-slate-50 border-slate-200'}`}>
                                                        {message.eval ? 'Y' : 'N'}
                                                    </span>
                                                </td>
                                                <td className="p-2 w-[52px] text-center align-middle">
                                                    <span className={`inline-flex items-center justify-center h-7 w-7 rounded-full border text-xs font-semibold ${message.auto_posted ? 'text-green-700 bg-green-50 border-green-200' : 'text-slate-700 bg-slate-50 border-slate-200'}`}>
                                                        {message.auto_posted ? 'Y' : 'N'}
                                                    </span>
                                                </td>
                                                <td className="relative p-2 w-[60px] text-center align-middle">
                                                    <div className="flex items-center justify-center">
                                                        <Tooltip content="Actions">
                                                            <Button
                                                                size="sm"
                                                                variant="text"
                                                                data-trans-message-actions-trigger="true"
                                                                onClick={(e) => openActionsMenu(message, e.currentTarget)}
                                                                className="w-8 h-8 p-2 rounded-full border border-slate-400 bg-white text-slate-700 hover:bg-slate-100 hover:border-slate-500"
                                                                aria-label="Actions"
                                                            >
                                                                <FaEllipsisV className="h-3 w-3" />
                                                            </Button>
                                                        </Tooltip>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </Card>
            </div>

            {/* Mobile: card list (sm) */}
            <div className="md:hidden w-full">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {(!messages || messages.length === 0) ? (
                        <div className="p-4 bg-white rounded-lg shadow-sm text-center">No messages found</div>
                    ) : (
                        messages.map((message) => {
                            const isEligibleForRequeue = message.eval && !message.posted_trans_id;
                            const isSelected = selectedMessages.has(message.id);
                            return (
                                <Card key={message.id} className="p-4 sm:p-5 bg-white shadow-lg hover:shadow-xl transition-all duration-200 rounded-xl border border-gray-100">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center gap-2 cursor-help flex-1 min-w-0">
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={(e) => handleSelectMessage(message.id, e.target.checked)}
                                                disabled={!isEligibleForRequeue}
                                                className="w-4 h-4 text-[#FF5733] bg-gray-100 border-gray-300 rounded focus:ring-[#FF5733] cursor-pointer flex-shrink-0"
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                            <Tooltip content={String(message.id)}>
                                                <Typography variant="small" className="font-semibold truncate">
                                                    {message.used_car || 'Plate No.: N/A'}
                                                </Typography>
                                            </Tooltip>
                                        </div>
                                        <div className="flex gap-1 flex-shrink-0 ml-2">
                                                <Tooltip content="View message">
                                                    <Button size="sm" variant="text" onClick={() => onViewMessage(message)} aria-label="View" className="w-8 h-8 p-2 rounded-full border border-blue-100 text-blue-600 hover:bg-blue-50 transition-colors"><FaEye className="h-3 w-3" /></Button>
                                                </Tooltip>

                                                {/* Mobile: collapse remaining actions into a dropdown menu */}
                                                    <div className="relative">
                                                        <Button
                                                            size="sm"
                                                            variant="text"
                                                            data-trans-message-actions-trigger="true"
                                                            onClick={(e) => openActionsMenu(message, e.currentTarget)}
                                                            className="w-8 h-8 p-2 rounded-full border border-slate-400 bg-white text-slate-700 hover:bg-slate-100 hover:border-slate-500"
                                                            aria-label="Actions"
                                                        >
                                                            <FaEllipsisV className="h-3 w-3" />
                                                        </Button>
                                                    </div>
                                                </div>
                                    </div>

                                    <div className="mb-3">
                                        <Typography variant="small" color="blue-gray" className="font-normal line-clamp-3">
                                            {message.text || 'No text content'}
                                        </Typography>
                                    </div>

                                    <div className="mb-3">
                                        <Typography variant="small" color="blue-gray" className="font-semibold mb-1">
                                            Transaction Code:
                                        </Typography>
                                        <Typography variant="small" color="blue-gray" className="font-mono bg-slate-50 px-2 py-1 rounded border border-slate-200 inline-block">
                                            {message.transaction_code || 'N/A'}
                                        </Typography>
                                    </div>

                                    <div className="flex flex-wrap gap-2 mb-3">
                                        {(() => {
                                            const statusMeta = getStatusIconMeta(getPostingStatus(message));
                                            return (
                                                <Tooltip content={`Status: ${statusMeta.label}`}>
                                                    <span className={`inline-flex items-center justify-center h-7 w-7 rounded-full border ${statusMeta.className}`}>
                                                        {statusMeta.icon}
                                                    </span>
                                                </Tooltip>
                                            );
                                        })()}
                                        <Tooltip content={message.posted_trans_id ? `Posted Trans ID: ${message.posted_trans_id}` : 'Not posted'}>
                                            <span className={`inline-flex items-center justify-center h-7 min-w-[28px] px-2 rounded-full border text-xs font-semibold ${message.posted_trans_id ? 'text-green-700 bg-green-50 border-green-200' : 'text-slate-700 bg-slate-50 border-slate-200'}`}>
                                                {message.posted_trans_id ? 'Y' : 'N'}
                                            </span>
                                        </Tooltip>
                                        <span className={`inline-flex items-center justify-center h-7 w-7 rounded-full border text-xs font-semibold ${message.eval ? 'text-green-700 bg-green-50 border-green-200' : 'text-slate-700 bg-slate-50 border-slate-200'}`}>
                                            {message.eval ? 'Y' : 'N'}
                                        </span>
                                        <span className={`inline-flex items-center justify-center h-7 w-7 rounded-full border text-xs font-semibold ${message.auto_posted ? 'text-green-700 bg-green-50 border-green-200' : 'text-slate-700 bg-slate-50 border-slate-200'}`}>
                                            {message.auto_posted ? 'Y' : 'N'}
                                        </span>
                                    </div>

                                    <Typography variant="small" color="blue-gray" className="font-normal mb-1">
                                        {message.used_driver ? `Driver: ${message.used_driver}` : 'Driver: N/A'}
                                    </Typography>
                                    <Typography variant="small" color="blue-gray" className="font-normal mb-1">
                                        {formatDate(message.timestamp || message.created_at)}
                                    </Typography>
                                    <Typography variant="small" color="blue-gray" className="font-medium mb-3 text-right">
                                        {formatAmount(message.amount_extracted)}
                                    </Typography>
                                </Card>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="flex-shrink-0 bg-white border-t border-blue-gray-100 p-4 rounded-xl shadow-sm">
                    {/* Desktop / tablet: full pagination */}
                    <div className="hidden sm:flex justify-between items-center">
                        <Typography variant="small" color="blue-gray" className="font-medium">Page {currentPage} of {totalPages} ({totalMessages} total)</Typography>
                        <div className="flex items-center gap-2">
                            <Button variant="outlined" size="sm" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className="flex items-center gap-1"><FaChevronLeft className="w-3 h-3" /> Previous</Button>
                            <div className="flex gap-1">{renderPaginationButtons()}</div>
                            <Button variant="outlined" size="sm" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} className="flex items-center gap-1">Next <FaChevronRight className="w-3 h-3" /></Button>
                        </div>
                    </div>

                    {/* Mobile: compact pagination */}
                    <div className="flex sm:hidden justify-between items-center">
                        <Button variant="outlined" size="sm" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className="flex items-center gap-1">
                            <FaChevronLeft className="w-3 h-3" />
                        </Button>
                        <div className="text-sm text-slate-600">{currentPage} / {totalPages}</div>
                        <Button variant="outlined" size="sm" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} className="flex items-center gap-1">
                            <FaChevronRight className="w-3 h-3" />
                        </Button>
                    </div>
                </div>
            )}

            {/* Content/Error Popup Dialog */}
            <Dialog open={popupOpen} handler={closePopup} size="lg">
                <DialogHeader>
                    <Typography variant="h5" color="blue-gray">{popupTitle}</Typography>
                </DialogHeader>
                <DialogBody divider className="max-h-[60vh] overflow-y-auto">
                    <Typography color="blue-gray" className="font-normal whitespace-pre-wrap">{popupContent}</Typography>
                </DialogBody>
                <DialogFooter>
                    <Button variant="text" color="red" onClick={closePopup} className="mr-1">Close</Button>
                </DialogFooter>
            </Dialog>

            {openMenu ? createPortal(
                <div
                    data-trans-message-actions-menu="true"
                    className="fixed w-44 overflow-hidden rounded-md border border-slate-200 bg-white shadow-2xl"
                    style={{
                        top: `${openMenu.top}px`,
                        left: `${openMenu.left}px`,
                        transform: openMenu.openUp ? 'translateY(-100%)' : 'none',
                        zIndex: 99999,
                    }}
                >
                    {(() => {
                        const message = messages.find((row) => row.id === openMenu.messageId);
                        if (!message) return null;

                        return (
                            <>
                                <button className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50" onClick={() => { onViewMessage(message); closeActionsMenu(); }}>
                                    <FaEye className="h-3 w-3 text-blue-600" />
                                    <span>View message</span>
                                </button>
                                {message.posting_error && !message.used && (
                                    <button className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50" onClick={() => { onRetryMessage(message); closeActionsMenu(); }}>
                                        <FaRedo className="h-3 w-3 text-orange-600" />
                                        <span>Retry posting</span>
                                    </button>
                                )}
                                {message.eval && !message.posting_error && !message.posted_trans_id && (
                                    <button className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50" onClick={() => { onRequeueMessage(message.id); closeActionsMenu(); }}>
                                        <FaPlayCircle className="h-3 w-3 text-purple-600" />
                                        <span>Requeue message</span>
                                    </button>
                                )}
                                {message.eval && !message.used && (
                                    <button className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50" onClick={() => { onMarkAsUsed(message.id); closeActionsMenu(); }}>
                                        <FaCheck className="h-3 w-3 text-green-600" />
                                        <span>Mark as used</span>
                                    </button>
                                )}
                                {!message.auto_posted && !message.used && (
                                    <button className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50" onClick={() => { onDeleteMessage(message); closeActionsMenu(); }}>
                                        <FaTrash className="h-3 w-3 text-red-600" />
                                        <span>Delete message</span>
                                    </button>
                                )}
                                <button className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50" onClick={() => { showPopup('Message Content', message.text || message.posting_error || 'No details'); closeActionsMenu(); }}>
                                    <FaInfoCircle className="h-3 w-3 text-slate-500" />
                                    <span>More details</span>
                                </button>
                            </>
                        );
                    })()}
                </div>,
                document.body
            ) : null}
        </div>
    );
};

export default TransMessagesTable;
